import styled from "styled-components";
import React, {useContext, useEffect, useState, useMemo, useCallback} from "react";
import {useMutation, useQueryClient} from '@tanstack/react-query';
import {Dropdown, Spinner} from "react-bootstrap";
import {SelectedWishlistContext} from "../../contexts/SelectedWishlistContext";
import {useCountryLang} from "../../hooks/useCountryLang";
import {useUserLoggedIn} from "../../hooks/useUserLoggedIn";
import {LoggedOutCartContext} from "../../contexts/LoggedOutCartContext";
import {ILoggedOutShoppingList} from "../../interfaces/ILoggedOutShoppingList";
import {getLocalStorageItem, setLocalStorageItem} from "../../shared/localStorageUtil";
import {LoggedOutWishlistContext} from "../../contexts/LoggedOutWishlistContext";
import {request} from "../../shared/httpInterceptor";
import {getTheme} from "../../shared/themeUtils";

// Define ReturnType of setTimeout for TypeScript
type TimeoutType = ReturnType<typeof setTimeout>;

const SelectionContain = styled.div`
  display: inline-block;
  
  .loading {
    margin-right: ${getTheme("space.2")};
  }
`
const InputQuantity = styled.input`
  width: 50px;
  text-align: center;
  border: 1px solid ${getTheme("colors.border")};
  border-radius: ${getTheme("borderRadius")};
`

const DropdownQuantity = styled.div`
  .btn-primary.dropdown-toggle {
    background-color: transparent;
    border-color: transparent;
    color: ${getTheme("colors.primary")};
  }

  .btn-primary.dropdown-toggle:focus {
    box-shadow: none;
  }

  .dropdown-menu {
    min-width: unset;
  }
`;

type UpdateProductQuantityPropsType = {
  productId: number,
  currentQuantity: number,
  maxQuantity?: number,
  className?: string,
  wishlistId?: number,
  type: "cart" | "wishlist"
}

const UpdateProductQuantity = (props: UpdateProductQuantityPropsType) => {
  const countryLang = useCountryLang()
  const {productId, currentQuantity, type, className} = props;
  const [quantity, setQuantity] = useState<number | ''>(currentQuantity)
  const queryClient = useQueryClient()
  const [isLoading, setIsLoading] = useState(false)
  const {selectedWishlist} = useContext(SelectedWishlistContext);
  const [showDropdown, setShowDropdown] = useState(currentQuantity < 5)
  const isUserLoggedIn = useUserLoggedIn();
  const {setLoggedOutCartCtx} = useContext(LoggedOutCartContext)
  const {setLoggedOutWishlistCtx} = useContext(LoggedOutWishlistContext)
  const [typingTimer, setTypingTimer] = useState<TimeoutType | null>(null)

  useEffect(() => {
    setQuantity(currentQuantity)
  }, [currentQuantity])
  
  const quantityOptions = useMemo(() => {
    const max = props.maxQuantity || 5;
    return Array.from({ length: max }, (_, i) => i + 1);
  }, [props.maxQuantity]);

  const handleLoggedOutCartItemUpdate = (updatedQuantity: number) => {
    const localStorageCart = getLocalStorageItem("loggedOutCart");
    if (localStorageCart != null) {
      const cartItems = JSON.parse(localStorageCart);
      const productIndex = cartItems.map((item: ILoggedOutShoppingList) => item.product.productID).indexOf(productId);
      cartItems[productIndex].quantity = updatedQuantity;
      setLocalStorageItem("loggedOutCart", JSON.stringify(cartItems));
      setLoggedOutCartCtx(cartItems);
    }
  }


  const deleteCartPositionMutation = useMutation(async () => {
    setIsLoading(true)
    await request({url: `/${countryLang}/deleteCartPosition?productId=${productId}`, method: 'delete'})
    await queryClient.cancelQueries(["shopping-cart", countryLang]);
    await queryClient.invalidateQueries(["shopping-cart", countryLang]);
    await queryClient.cancelQueries(["checkout-shopping-cart", countryLang]);
    await queryClient.invalidateQueries(["checkout-shopping-cart", countryLang]);
    setIsLoading(false)
  })


  const updateCartPositionMutation = useMutation(async (itemQuantity: number) => {
    setIsLoading(true)
    if (!isUserLoggedIn) {
      handleLoggedOutCartItemUpdate(itemQuantity)
    } else {
      const item = {
        productId,
        quantity: itemQuantity
      }
      await request({url: `/${countryLang}/updateCartItem`, data: item, method: "post"})
      await queryClient.cancelQueries(["shopping-cart", countryLang]);
      await queryClient.invalidateQueries(["shopping-cart", countryLang]);
      await queryClient.cancelQueries(["checkout-shopping-cart", countryLang]);
      await queryClient.invalidateQueries(["checkout-shopping-cart", countryLang]);
    }
    setIsLoading(false)
  })


  const updateWishlistPositionMutation = useMutation(async (itemQuantity: number) => {
    setIsLoading(true)
    if (!isUserLoggedIn) {
      handleLoggedOutWishlistItemUpdate()
    } else {
      const item = {
        wishlistId: props.wishlistId ? props.wishlistId : selectedWishlist?.id,
        productId,
        quantity: itemQuantity
      }
      await request({url: `/${countryLang}/wishlist/position/edit`, data: item, method: "post"})
      await queryClient.invalidateQueries(["wishlists", countryLang])
      await queryClient.cancelQueries(["wishlist", props.wishlistId ? props.wishlistId : selectedWishlist?.id]);
      await queryClient.invalidateQueries(["wishlist", props.wishlistId ? props.wishlistId : selectedWishlist?.id]);
    }
    setIsLoading(false)
  })


  const handleLoggedOutWishlistItemUpdate = () => {
    const localStorageWishlist = getLocalStorageItem("loggedOutWishlist")
    if (localStorageWishlist!=null) {
      const wishlistItems = JSON.parse(localStorageWishlist)
      const productIndex = wishlistItems.map((item: ILoggedOutShoppingList) => item.product.productID).indexOf(productId)
      wishlistItems[productIndex].quantity = quantity;
      setLocalStorageItem("loggedOutWishlist", JSON.stringify(wishlistItems))
      setLoggedOutWishlistCtx(wishlistItems)
    }
  }


  const deleteWishlistPositionMutation = useMutation(async () => {
    setIsLoading(true)
    await request({url:`/${countryLang}/wishlist/position/delete?wishlistId=${selectedWishlist?.id}&productId=${productId}`, method: "delete"})
    await queryClient.cancelQueries(["wishlist", selectedWishlist?.id]);
    await queryClient.invalidateQueries(["wishlist", selectedWishlist?.id]);
    setIsLoading(false)
  })

  const handleInputQuantity = (e: any) => {
    const value = e.target.value;
    
    if (value === '') {
      setQuantity('');
      return;
    }
    
    const numValue = parseInt(value);
    if (!isNaN(numValue) && numValue > 0) {
      if (props.maxQuantity && numValue > props.maxQuantity) {
        setQuantity(props.maxQuantity);
      } else {
        setQuantity(numValue);
        
        // If quantity changes from >=5 to <5, immediately update and switch to dropdown
        if (numValue < 5 && typeof quantity === 'number' && quantity >= 5) {
          // Clear any pending timers
          if (typingTimer) {
            clearTimeout(typingTimer);
            setTypingTimer(null);
          }
          
          // Force immediate update
          setTimeout(() => {
            actionAndUpdate(numValue);
          }, 0);
        }
      }
    }
  }

  const handleDropdownSelect = (key: any) => {
    const selectedValue = parseInt(key);
    setQuantity(selectedValue);
    
    // Set the display mode based on the value
    if (selectedValue === 5) {
      setShowDropdown(false);
    }
    
    // Always persist dropdown selections immediately
    actionAndUpdate(selectedValue);
  }

  const handleOnEnter = (e: any) => {
    if (e.key === 'Enter') {
      // Make sure to parse the input value as a number
      const value = parseInt(e.target.value);
      if (!isNaN(value) && value > 0) {
        actionAndUpdate(value);
      }
    }
  }

  const handleInputBlur = () => {
    // Clear any existing timer to prevent duplicate API calls
    if (typingTimer) {
      clearTimeout(typingTimer);
      setTypingTimer(null);
    }
    
    if (typeof quantity === 'number' && quantity > 0) {
      actionAndUpdate(quantity);
    }
  }

  const actionAndUpdate = useCallback((itemQuantity: number) => {
    if (typeof itemQuantity === 'number' && itemQuantity > 0) {
      type=="cart" && updateCartPositionMutation.mutate(itemQuantity)
      type=="wishlist" && updateWishlistPositionMutation.mutate(itemQuantity)
    } else {
      type=="cart" && deleteCartPositionMutation.mutate()
      type=="wishlist" && deleteWishlistPositionMutation.mutate()
    }

    // Update the display mode based on the new quantity
    if (typeof itemQuantity === 'number') {
      if (itemQuantity < 5) {
        setShowDropdown(true);
      } else {
        setShowDropdown(false);
      }
    }
  }, [type, updateCartPositionMutation, updateWishlistPositionMutation, deleteCartPositionMutation, deleteWishlistPositionMutation])

  // Add effect to handle auto-persisting changes for quantities >= 5
  useEffect(() => {
    // Only auto-persist if we're in input mode and the quantity is 5 or greater
    if (typeof quantity === 'number' && quantity >= 5 && !showDropdown) {
      // Clear any existing timer
      if (typingTimer) clearTimeout(typingTimer)
      
      // Set a new timer to persist changes after a short delay
      const timer = setTimeout(() => {
        actionAndUpdate(quantity)
      }, 500)
      
      setTypingTimer(timer)
      
      // Clean up on unmount or when quantity changes
      return () => {
        if (timer) clearTimeout(timer)
      }
    }
  }, [quantity, showDropdown, typingTimer, actionAndUpdate])

  const renderQuantitySelect = <DropdownQuantity>
    <Dropdown onSelect={handleDropdownSelect}>
      <Dropdown.Toggle id="dropdown-basic">
        <span className="quantity">{quantity}</span>
      </Dropdown.Toggle>
      <Dropdown.Menu>
        {quantityOptions.map(num => (
          <Dropdown.Item key={num} eventKey={num}>{num}</Dropdown.Item>
        ))}
      </Dropdown.Menu>
    </Dropdown>
  </DropdownQuantity>

  const renderQuantityInput = <InputQuantity
    className={className}
    type="text"
    pattern="[0-9]*"
    inputMode="numeric"
    value={quantity}
    onChange={handleInputQuantity}
    onBlur={handleInputBlur}
    onKeyUp={handleOnEnter}
  />

  return (
    <SelectionContain>
      {(!isLoading && typeof quantity === 'number' && quantity < 5 && showDropdown) && renderQuantitySelect}
      {(!isLoading && (typeof quantity === 'number' && quantity >= 5 || !showDropdown)) && renderQuantityInput}
      {isLoading && <span className="loading"><Spinner animation="border" size="sm"/></span>}
    </SelectionContain>
  )
}

export default UpdateProductQuantity
